import {
  BlockDomain,
  BlockDraftWorkoutDomain,
  TeamDetail,
  WorkoutLayerDomain,
} from '@strength/domain';
import { DateTime } from 'luxon';
import { v4 } from 'uuid';
import { z } from 'zod';

/** Builds the input for `trpc.plan.createTrack` for a new single-athlete track. */
export function buildTrackInput({
  name,
  profileId,
}: {
  name: string;
  profileId: string;
}) {
  return {
    id: v4(),
    name,
    athletes: [{ id: v4(), profileId }],
  };
}

type CreateBlockInput = z.infer<typeof BlockDomain.Create.shape.input>;

export type CreateSessionParams = {
  teamDetail: TeamDetail;
  name: string;
  trackId: string;
  /** ISO date (yyyy-MM-dd) of the clicked calendar day. */
  startDate: string;
  /** Optional single focus; mapped to a one-element `focuses` array. */
  focusId: string | null;
  trainingMaxRatio: number;
};

/**
 * Build a `Block` Create input for a single-day "session": one training week,
 * one training day (`layout = { 1: 1 }`), strength training only, always dated
 * to the clicked day (never an undated/flex workout). Mirrors `createBlockInput`
 * from the timeline form, with the session-specific values fixed.
 */
export function buildSessionInput({
  teamDetail,
  name,
  trackId,
  startDate,
  focusId,
  trainingMaxRatio,
}: CreateSessionParams): CreateBlockInput {
  const start = DateTime.fromISO(startDate);
  if (!start.isValid) {
    throw new Error('Invalid start date');
  }

  // One cycle (week); end date follows the same rule the timeline form uses.
  const endDate = start
    .plus({ days: BlockDomain.DEFAULT_CYCLE_LENGTH_IN_DAYS - 1 })
    .toISODate();
  if (!endDate) {
    throw new Error('Invalid end date');
  }

  return {
    id: v4(),
    name,
    focuses: focusId ? [{ id: v4(), focusId, sortOrder: 0 }] : [],
    blockType: 'strength_training',
    startDate,
    endDate,
    trainingMaxRatio,
    cycleLabel: teamDetail.settings.defaultCycleLabel,
    sessionLabel: teamDetail.settings.defaultSessionLabel,
    trackId,
    layout: { 1: 1 },
    autoSchedule: false,
    allowFlexWorkouts: false,
  };
}

/**
 * Build a loco-sync command that pre-dates the session's single workout to the
 * clicked calendar day. The builder reconciles the layer to the `{1:1}` grid
 * cell by `(cycleSortOrder, sessionSortOrder)`.
 */
export function buildSessionWorkoutCommand({
  blockId,
  startDate,
}: {
  blockId: string;
  /** ISO date (yyyy-MM-dd) of the clicked calendar day. */
  startDate: string;
}): BlockDraftWorkoutDomain.Command {
  return {
    module: BlockDraftWorkoutDomain.Module,
    identity: { blockId, workoutId: v4() },
    edits: [
      {
        modelName: 'BlockDraftWorkoutLayer',
        modelId: v4(),
        action: 'create',
        data: {
          cycleSortOrder: 1,
          sessionSortOrder: 1,
          assignedDate: startDate,
          layer: WorkoutLayerDomain.defaultLayer(),
          name: null,
        },
      },
    ],
  };
}
